<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import ImprintsFilters from '../imprints/ImprintsFilters.vue';
  import AutorsTable from '../imprints/AutorsTable.vue';
  import FavoritePublishers from './FavoritePublishers.vue';
  import Pagination from '../Pagination.vue';

  const { t } = useI18n();

  const props = defineProps({
    audiences: { type: Array, required: true },
    languages: { type: Array, required: true },
    recentPath: { type: String, required: true },
    userSignedIn: { type: Boolean, default: false }
  });

  const territories = ref([]);
  const countriesOfPublication = ref([]);
  const publishers = ref([]);
  const languageValues = ref([]);
  const totalItems = ref(0);
  const itemsPerPage = ref(48);
  const currentPage = ref(1);
  const isLoading = ref(false);
  const selectedPublisher = ref(null);
  const activeTab = ref('all');
  const followingInProgress = ref(null);
  const toastMessage = ref('');
  const toastMessageType = ref('success');

  const filterData = ref({
    searchQuery: '',
    selectedAudience: '',
    selectedLanguage: '',
    selectedTerritory: '',
    selectedCountryOfPublication: '',
    sortBy: 'publication_count',
    showOnlyFollowed: false
  });

  const columns = computed(() => {
    if (!publishers.value.length) return [];
    const count = Math.min(4, Math.max(1, Math.ceil(publishers.value.length / 12)));
    return Array.from({ length: count }, (_, i) =>
      publishers.value.slice(i * 12, (i + 1) * 12)
    );
  });

  const publisherUrl = (id) => {
    const languages = languageValues.value.length ? languageValues.value.join(',') : 'all';
    return `${props.recentPath}?imprint_ids=${id}&languages=${encodeURIComponent(languages)}`;
  };
  const truncate = (str, n) => str?.length > n ? str.slice(0, n) + '…' : str;

  const fetchPublishers = async () => {
    isLoading.value = true;
    try {
      const params = new URLSearchParams({ page: currentPage.value, per_page: itemsPerPage.value });
      const { searchQuery, selectedAudience, selectedLanguage, selectedTerritory, selectedCountryOfPublication, sortBy } = filterData.value;
      if (searchQuery) params.append('search', searchQuery);
      if (selectedAudience) params.append('audience', selectedAudience);
      if (selectedLanguage) params.append('language', selectedLanguage);
      if (selectedTerritory) params.append('territory', selectedTerritory);
      if (selectedCountryOfPublication) params.append('country_of_publication', selectedCountryOfPublication);
      if (sortBy && sortBy !== 'publication_count') params.append('sort_by', sortBy);
      if (filterData.value.showOnlyFollowed) params.append('followed', 'true');

      const response = await axios.get(`/publishers.json?${params}`);
      publishers.value = response.data.publishers || [];
      languageValues.value = response.data.language_values || [];
      totalItems.value = response.data.total || 0;
    } catch (error) {
      publishers.value = [];
      languageValues.value = [];
      totalItems.value = 0;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTerritories = async () => {
    try {
      const response = await axios.get('/api/imprints/territories.json');
      territories.value = response.data;
    } catch {
      // non-critical
    }
  };

  const fetchCountriesOfPublication = async () => {
    try {
      const response = await axios.get('/api/imprints/countries_of_publication.json');
      countriesOfPublication.value = response.data;
    } catch {
      // non-critical
    }
  };

  const onFilterChange = () => {
    currentPage.value = 1;
    fetchPublishers();
  };

  const handlePageChange = (page) => {
    currentPage.value = page;
    fetchPublishers();
  };

  const handleViewAuthors = (publisher) => {
    selectedPublisher.value = { id: publisher.id, name: publisher.name };
  };

  const showToast = (message, type = 'success') => {
    toastMessage.value = message;
    toastMessageType.value = type;
    const toastEl = document.getElementById('publisherFollowToast');
    const toast = new bootstrap.Toast(toastEl);
    toast.show();
  };

  const toggleFollowing = async (publisher) => {
    if (followingInProgress.value === publisher.id) return;

    try {
      followingInProgress.value = publisher.id;
      const isCurrentlyFollowed = publisher.is_followed;
      const url = `/api/imprints/${publisher.id}/${isCurrentlyFollowed ? 'unfollow' : 'follow'}.json`;
      const method = isCurrentlyFollowed ? 'delete' : 'post';

      const response = await axios[method](url);

      if (response.data.success) {
        publisher.is_followed = response.data.is_followed;
        const message = response.data.is_followed
          ? t('components.imprints_list.toast.now_following', { name: publisher.name })
          : t('components.imprints_list.toast.unfollowed', { name: publisher.name });
        showToast(message, 'success');
      }
    } catch {
      showToast(t('components.imprints_list.toast.error'), 'error');
    } finally {
      followingInProgress.value = null;
    }
  };

  onMounted(() => {
    fetchTerritories();
    fetchCountriesOfPublication();
    fetchPublishers();
  });
</script>

<template>
  <div>
    <div :class="['align-items-center border-0', toastMessageType === 'error' ? 'toast_error toast' : 'toast']" role="alert" aria-live="assertive" aria-atomic="true" id="publisherFollowToast">
      <div class="d-flex">
        <div class="toast-body">{{ toastMessage }}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>

    <AutorsTable
      v-if="selectedPublisher"
      :imprint-id="selectedPublisher.id"
      :imprint-name="selectedPublisher.name"
      @back="selectedPublisher = null"
    />

    <template v-else>
      <ul v-if="userSignedIn" class="nav nav-tabs mb-4">
        <li class="nav-item">
          <button class="nav-link" :class="{ active: activeTab === 'all' }" @click="activeTab = 'all'">
            {{ t('components.imprints_list.all_publishers') }}
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{ active: activeTab === 'favorites' }" @click="activeTab = 'favorites'">
            {{ t('components.imprints_list.favorite_publishers') }}
          </button>
        </li>
      </ul>

      <FavoritePublishers
        v-if="activeTab === 'favorites'"
        :recent-path="recentPath"
      />

      <template v-else>
      <ImprintsFilters
        v-bind="filterData"
        :audiences="audiences"
        :languages="languages"
        :territories="territories"
        :countries-of-publication="countriesOfPublication"
        :show-followed-filter="false"
        @search="(q) => { filterData.searchQuery = q; onFilterChange(); }"
        @audience-change="(v) => { filterData.selectedAudience = v; onFilterChange(); }"
        @language-change="(v) => { filterData.selectedLanguage = v; onFilterChange(); }"
        @territory-change="(v) => { filterData.selectedTerritory = v; onFilterChange(); }"
        @country-of-publication-change="(v) => { filterData.selectedCountryOfPublication = v; onFilterChange(); }"
      />

      <div class="d-flex align-items-center justify-content-between mb-3">
        <div v-if="userSignedIn" class="form-check form-switch mb-0">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="showOnlyFollowed"
            v-model="filterData.showOnlyFollowed"
            @change="onFilterChange"
          />
          <label class="form-check-label" for="showOnlyFollowed">
            {{ t('components.imprints_list.filters.show_only_followed') }}
          </label>
        </div>
        <div v-else></div>
        <div class="d-flex align-items-center gap-2">
          <label class="text-nowrap">{{ t('components.imprints_list.filters.sort_by') }}</label>
          <select
            class="form-select form-select-sm w-auto"
            v-model="filterData.sortBy"
            @change="onFilterChange"
          >
            <option value="publication_count">{{ t('components.imprints_list.filters.sort_publication_count') }}</option>
            <option value="popularity">{{ t('components.imprints_list.filters.sort_popularity') }}</option>
            <option value="bestsellers">{{ t('components.imprints_list.filters.sort_bestsellers') }}</option>
            <option value="name">{{ t('components.imprints_list.filters.sort_name') }}</option>
          </select>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">{{ t('components.imprints_list.loading') }}</span>
        </div>
      </div>

      <template v-else>
        <div v-if="publishers.length === 0" class="row mt-3 mb-3">
          <div class="col">
            <h4>{{ t('components.imprints_list.no_results') }}</h4>
          </div>
        </div>

        <div v-else class="row mt-3 mb-3">
          <div v-for="(col, i) in columns" :key="i" class="col-3">
            <div v-for="publisher in col" :key="publisher.id" class="publishers-list__item">
              <a :href="publisherUrl(publisher.id)" class="facet__option" target="_blank" rel="noopener noreferrer">
                <span class="publishers-list__count">
                  {{ publisher.publication_count.toLocaleString() }}
                  <i
                    class="bi bi-people-fill publishers-list__authors-icon"
                    @click.prevent="handleViewAuthors(publisher)"
                    data-bs-toggle="tooltip"
                    :data-bs-title="t('components.imprints_list.view_authors')"
                    :aria-label="t('components.imprints_list.view_authors')"
                  ></i>
                  <i
                    v-if="userSignedIn"
                    :class="['publishers-list__follow-icon', publisher.is_followed ? 'bi bi-plus-circle-fill' : 'bi bi-plus-circle']"
                    @click.prevent="toggleFollowing(publisher)"
                    data-bs-toggle="tooltip"
                    :data-bs-title="publisher.is_followed ? t('components.imprints_list.unfollow_publisher') : t('components.imprints_list.follow_publisher')"
                    :aria-label="publisher.is_followed ? t('components.imprints_list.unfollow_publisher') : t('components.imprints_list.follow_publisher')"
                  ></i>
                </span>
                <span class="publishers-list__name">{{ truncate(publisher.name, 28) }}</span>
              </a>
            </div>
          </div>
        </div>

        <Pagination
          class="mt-5"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="itemsPerPage"
          @page-change="handlePageChange"
        />
      </template>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .publishers-list__count {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }

  .publishers-list__follow-icon,
  .publishers-list__authors-icon {
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--primary);

    &:hover {
      opacity: 0.8;
    }
  }
</style>
